import React, { useContext } from 'react';
import { formatTons, formatVolume, formatWeight, isNotBlank, translateQuantityCode, weightAndDimensions } from '@utils/utils';
import {
  dayjsFromApiUtcInTz,
  formatDateFromDayjsWithLocale,
  formatTimeIntervalFromDayjsIntervalWithLocale,
} from '@utils/dateWithTz.tsx';
import styled from 'styled-components';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { useTranslation } from 'react-i18next';
import { DemandDetailContext } from '@app/pages/carrier/demand-detail/DemandDetailActions.tsx';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import PanelWithIcon from '@components/obsolete/PanelWithIcon/PanelWithIcon.tsx';
import TimeList from '@components/obsolete/TimeList/TimeList.tsx';
import TimeListItem from '@components/obsolete/TimeList/TimeListItem.tsx';
import LoadInfoList from '@components/obsolete/LoadDetails/LoadInfoList/LoadInfoList.tsx';
import TimeslotsProgressTitle from '@components/obsolete/TimeslotsProgress/TimeslotsProgressTitle.tsx';
import DisplayLocation from '@components/obsolete/DisplayLocation/DisplayLocation.tsx';
import ListInfoItem from '@components/obsolete/ListInfo/ListInfoItem.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';


interface ComponentProps {
  enableAction: boolean;
  locationDisplayPattern: string;
}

const RouteDescriptionComponent: React.FC<ComponentProps> = ({ enableAction, locationDisplayPattern }) => {
  const { t } = useTranslation('common');
  const { tr } = useTranslationLgs();

  const { state } = useContext(DemandDetailContext);

  return (
    <>
      <div className="route-description mt-8">
          <hr className="p-mt-0 p-mb-5" />
          <>
            <Label title={t('RouteDescriptionComponent.routePlan', 'Plán cesty')} />
            {state.timeslots.map((location, locationIndex: number) => {
              return (
                <div className="timeslots-wrapper" key={locationIndex}>
                  <PanelWithIcon numberIcon={`${locationIndex + 1}`} wrapper="SM">
                    <p>
                      <DisplayLocation location={location} locationDisplayPattern={locationDisplayPattern} />
                      <COTimezone dataTestId={`carrier-demand-routePlan-location-${locationIndex}-timezone`} timezoneId={location.timeslots[0]?.timezoneId}/>
                    </p>
                  </PanelWithIcon>
                  <div className="time-shell">
                    <TimeList>
                      {location.timeslots.map((timeslot, timeslotIndex: number) => {
                        const slotSince = timeslot.timezoneId != null ? timeslot.utcStart : timeslot.sinceInUTC;
                        const slotTill = timeslot.timezoneId != null ? timeslot.utcEnd : timeslot.tillInUTC;
                        const timezoneId =  timeslot.timezoneId ?? 'Europe/Prague';
                        return (
                          <TimeListItem
                            key={timeslotIndex}
                            date={formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(slotSince, timezoneId))}
                            isWholeDay={timeslot.isNotSpecifiedTime}
                            time={`${formatTimeIntervalFromDayjsIntervalWithLocale(dayjsFromApiUtcInTz(slotSince, timezoneId), dayjsFromApiUtcInTz(slotTill, timezoneId), timezoneId)}`}
                            selected={timeslot.isSelected}
                            timezoneId={timeslot.timezoneId}
                            dataTestId={`carrier-demand-routePlan-location-${locationIndex}-slot-${timeslotIndex}`}
                          />
                        );
                      })}
                    </TimeList>
                  </div>
                </div>
              );
            })}
          </>
          <div className="shipment-requirements-wrapper">
            <Label title={t('RouteDescriptionComponent.manipulationType', 'Manipulace')} />
            <p>
              <i className="p-mr-2 pi pi-check"></i>
              <span>{state.loadingTypes && state.loadingTypes.map((type: string) => t(`RouteDescriptionComponent.manipulatioType.${type}`)).join(', ')}</span>
            </p>
          </div>
          {state.items && state.items.length > 0 && (
            <div className="load-info-wrapper load-info-wrapper--sm  p-mt-4 p-mb-4 p-d-flex p-flex-wrap">
              {state.items.map((cargo, cargoIndex) => {
                return (
                  <LoadInfoList
                    key={cargoIndex}
                    title= {tr(`DemandShipmentDetail.cargoTemplateName`, `{{unit}} ({{count}} x)`, {
                      unit: cargo.name,
                      count: cargo.amount,
                    })}
                    description={formatTons(cargo.weight)}
                    stackable={cargo.stackable}
                    itemNote={cargo.itemNote}
                  />
                );
              })}
            </div>
          )}
          <div className="p-mt-5">
            {state.locations.map((location, locationIndex: number) => {
              return (
                <React.Fragment key={locationIndex}>
                  <TimeslotsProgressTitle
                    number={`${locationIndex + 1}`}
                    address={<DisplayLocation location={location} locationDisplayPattern={locationDisplayPattern} />}
                    timezoneId={location.timezoneId}
                  />
                  {location.requirementCodes && location.requirementCodes.length > 0 && (
                    <div className="pb-2 gap-1">
                      <strong>{t('RouteDescriptionComponent.requirements', 'Požadavky')}:</strong>
                      {location.requirementCodes.map(req => tr('specialRequirement.' + req, req)).join(', ')}
                    </div>
                  )}
                  {location.loadedOrders && location.loadedOrders.length > 0 && (
                    <div className="p-d-flex">
                      <PanelWithIcon srcIcon="/images/icon_pickup.png">
                        <Label title={t('RouteDescriptionComponent.pickUp', 'Nakládka')} />

                        {location.loadedOrders && location.loadedOrders.length > 0 && (
                          <>
                            {location.loadedOrders.map((loadedOrder, loadedOrderKey: number) => {
                              return (
                                <Col key={loadedOrderKey}>
                                  {loadedOrder.orderName && (
                                    <Row>
                                      <ListInfoItem title={t(`RouteDescriptionComponent.order`, `Objednavka`)}>
                                        <p>
                                          <small>{loadedOrder.orderName}</small>
                                        </p>
                                      </ListInfoItem>
                                      <ListInfoItem title={t(`RouteDescriptionComponent.weight`, `Hmotnost`)}>
                                        <p>
                                          <small>{formatWeight(loadedOrder.totalWeight)}</small>
                                        </p>
                                      </ListInfoItem>
                                      {state.carrierQuoteSettings?.volumeOfOrderEnabled && (
                                        <ListInfoItem title={t(`RouteDescriptionComponent.volume`, `Objem`)}>
                                          <p>
                                            <small>{formatVolume(loadedOrder.totalVolume)}</small>
                                          </p>
                                        </ListInfoItem>
                                      )}
                                    </Row>
                                  )}
                                  {loadedOrder.items.map((cargo, cargoIndex: number) => {
                                    return (
                                      <React.Fragment key={cargoIndex}>
                                        <LoadInfoList
                                          title={`${cargo.name} (${cargo.amount} ${translateQuantityCode(cargo.quantityUnit, tr)})`}
                                          description={weightAndDimensions(cargo.height, cargo.length, cargo.width, cargo.weight, t)}
                                          stackable={cargo.stackable}
                                          itemNote={cargo.itemNote}
                                        />
                                        {cargo.manipulationUnitInfo && cargo.manipulationUnitInfo.length > 0 && (
                                          <div>
                                            <span>{tr(`RingilOrder.manipulationUnitInfoLabel`, `Manipulation units:`)}</span>
                                            <div className="flex flex-col">
                                              {cargo.manipulationUnitInfo
                                                .map(mu =>
                                                  [
                                                    isNotBlank(mu.externalId) ? mu.externalId : null,
                                                    isNotBlank(mu.name) ? mu.name : null,
                                                    mu.count != null ? `${mu.count}` : null,
                                                    mu.weight != null && mu.weight > 0 ? formatWeight(mu.weight) : null,
                                                    isNotBlank(mu.size) ? mu.size : null,
                                                    isNotBlank(mu.wheelbase) ? mu.wheelbase : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(', '),
                                                )
                                                .map((unit, unitIndex) => <span key={unitIndex}>{unit}</span>)}
                                            </div>
                                          </div>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </Col>
                              );
                            })}
                          </>
                        )}
                      </PanelWithIcon>
                    </div>
                  )}
                  {location.unloadedOrders && location.unloadedOrders.length > 0 && (
                    <div className="p-d-flex">
                      <PanelWithIcon srcIcon="/images/icon_dropoff.png">
                        <Label title={t('RouteDescriptionComponent.dropoff', 'Vykládka')} />

                        {location.unloadedOrders && location.unloadedOrders.length > 0 && (
                          <>
                            {location.unloadedOrders.map((loadedOrder, loadedOrderKey: number) => {
                              return (
                                <Col key={loadedOrderKey}>
                                  {loadedOrder.orderName && (
                                    <Row>
                                      <ListInfoItem title={t(`RouteDescriptionComponent.order`, `Objednavka`)}>
                                        <p>
                                          <small>{loadedOrder.orderName}</small>
                                        </p>
                                      </ListInfoItem>
                                      <ListInfoItem title={t(`RouteDescriptionComponent.weight`, `Hmotnost`)}>
                                        <p>
                                          <small>{formatWeight(loadedOrder.totalWeight)}</small>
                                        </p>
                                      </ListInfoItem>
                                      {state.carrierQuoteSettings?.volumeOfOrderEnabled && (
                                        <ListInfoItem title={t(`RouteDescriptionComponent.volume`, `Objem`)}>
                                          <p>
                                            <small>{formatVolume(loadedOrder.totalVolume)}</small>
                                          </p>
                                        </ListInfoItem>
                                      )}
                                    </Row>
                                  )}
                                  {loadedOrder.items.map((cargo, cargoIndex: number) => {
                                    return (
                                      <React.Fragment key={cargoIndex}>
                                        <LoadInfoList
                                          title={`${cargo.name} (${cargo.amount} ${translateQuantityCode(cargo.quantityUnit, tr)})`}
                                          description={weightAndDimensions(cargo.height, cargo.length, cargo.width, cargo.weight, t)}
                                          stackable={cargo.stackable}
                                          itemNote={cargo.itemNote}
                                        />
                                        {cargo.manipulationUnitInfo && cargo.manipulationUnitInfo.length > 0 && (
                                          <div>
                                            <span>{tr(`RingilOrder.manipulationUnitInfoLabel`, `Manipulation units:`)}</span>
                                            <div className="flex flex-col">
                                              {cargo.manipulationUnitInfo
                                                .map(mu =>
                                                  [
                                                    isNotBlank(mu.externalId) ? mu.externalId : null,
                                                    isNotBlank(mu.name) ? mu.name : null,
                                                    mu.count != null ? `${mu.count}` : null,
                                                    mu.weight != null && mu.weight > 0 ? formatWeight(mu.weight) : null,
                                                    isNotBlank(mu.size) ? mu.size : null,
                                                    isNotBlank(mu.wheelbase) ? mu.wheelbase : null,
                                                  ]
                                                    .filter(Boolean)
                                                    .join(', '),
                                                )
                                                .map((unit, unitIndex) => <span key={unitIndex}>{unit}</span>)}
                                            </div>
                                          </div>
                                        )}
                                      </React.Fragment>

                                    );
                                  })}
                                </Col>
                              );
                            })}
                          </>
                        )}
                      </PanelWithIcon>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="p-mt-5">
            <h3 className="p-mt-0" style={{ fontWeight: 'normal' }}>
              {t('RouteDescriptionComponent.customer', 'Poptávající')}
            </h3>
            <PanelWithIcon srcIcon="/images/contacts.svg">
              <Label title={state.customer.name} />
              <p>
                {state.customer.streetNr}, {state.customer.city}, {state.customer.country}
              </p>
              <p>
                {t('RouteDescriptionComponent.CrnAndTin', 'IČ: 28441345 | DIČ: CZ28441345', {
                  crn: state.customer.crn || '-',
                  tin: state.customer.tin || '-',
                })}
              </p>
            </PanelWithIcon>

            <PanelWithIcon srcIcon="/images/user.svg">
              <p>{state.responsiblePerson.fullName}</p>
              <p className="text-gray5">
                <small>{t('RouteDescriptionComponent.responsiblePerson', 'odpovědná osoba')}</small>
              </p>
            </PanelWithIcon>
          </div>
      </div>
    </>
  );
};

const Col = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
`;

export default RouteDescriptionComponent;
